/**
 * Formats an ISO 8601 timestamp as the app-wide house-style date string
 * `Mon D, YYYY` (e.g. `Jul 6, 2026`).
 *
 * This is the single source of truth for date-only display across Accedo. Use it
 * everywhere a stored timestamp is shown to a user so the spelled-month style stays
 * consistent app-wide and cannot drift back to the bare numeric `M/D/YYYY` form.
 *
 * The same `{ year, month, day }` options object matches `formatRelativeTimestamp`'s
 * absolute-date branch (`src/utils/activityLog.ts`), so surfaces that mix the two —
 * e.g. an Acknowledgments row showing both a "Due" subtitle and a "Sent" value — read
 * as one house style.
 *
 * For date-only `YYYY-MM-DD` strings that must preserve the local calendar day in
 * negative-UTC-offset timezones, use `formatDueDate` instead — it constructs the Date
 * locally to avoid the UTC-midnight off-by-one.
 *
 * @param iso - An ISO 8601 timestamp string.
 * @returns A locale-formatted date string in the `Mon D, YYYY` style.
 */
export function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
